import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search,
    Eye,
    Edit,
    Trash2,
    Plus,
    RefreshCw,
    Building2,
    MapPin,
    Users,
    CheckCircle,
    XCircle,
    Filter,
    Image as ImageIcon,
    Calendar,
    X,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import {
    useGetAllApartmentsQuery,
    useDeleteApartmentMutation,
    useManageApartmentAvailabilityMutation,
    Apartment
} from '../../store/slices/apiSlice';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';

const ApartmentManagement = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'true' | 'false'>('all');
    const [locationFilter, setLocationFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    
    // Modal states
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
    const [selectedApartment, setSelectedApartment] = useState<Apartment | null>(null);


    // Availability form state
    const [availabilityForm, setAvailabilityForm] = useState({
        dates: [] as string[],
        is_available: false,
        reason: ''
    });

    // Fetch apartments
    const filterParams: { is_active?: boolean; location?: string } = {};
    if (statusFilter !== 'all') {
        filterParams.is_active = statusFilter === 'true';
    }
    if (locationFilter) {
        filterParams.location = locationFilter;
    }

    const { data, isLoading, error, refetch } = useGetAllApartmentsQuery(filterParams);
    const [deleteApartment, { isLoading: isDeleting }] = useDeleteApartmentMutation();
    const [manageAvailability, { isLoading: isManagingAvailability }] = useManageApartmentAvailabilityMutation();

    // Extract apartments from API response
    const apartments = data?.data || [];

    // Filter apartments based on search
    const filteredApartments = apartments.filter((apartment: Apartment) => {
        const matchesSearch =
            apartment.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            apartment.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            apartment.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            apartment.description?.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
    });

    // Pagination logic
    const totalPages = Math.ceil(filteredApartments.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedApartments = filteredApartments.slice(startIndex, startIndex + itemsPerPage);

    // Statistics
    const activeApartments = apartments.filter(a => a.is_active).length;
    const availableApartments = apartments.filter(a => a.is_available && a.is_active).length;
    const totalApartments = apartments.length;


    // Handle create - navigate to create page
    const handleCreate = () => {
        navigate('/admin/apartments/create');
    };

    // Handle edit - navigate to edit page
    const handleEdit = (apartment: Apartment) => {
        navigate(`/admin/apartments/edit/${apartment.apartment_id}`);
    };

    // Handle view - navigate to view page
    const handleView = (apartment: Apartment) => {
        navigate(`/admin/apartments/view/${apartment.apartment_id}`);
    };

    // Handle items per page change
    const handleItemsPerPageChange = (newItemsPerPage: number) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1); // Reset to first page when changing items per page
    };

    // Handle delete modal open
    const handleDeleteModalOpen = (apartment: Apartment) => {
        setSelectedApartment(apartment);
        setShowDeleteModal(true);
    };

    // Handle availability modal open
    const handleAvailabilityModalOpen = (apartment: Apartment) => {
        setSelectedApartment(apartment);
        setAvailabilityForm({
            dates: [],
            is_available: false,
            reason: ''
        });
        setShowAvailabilityModal(true);
    };




    // Handle delete
    const handleDelete = async () => {
        if (!selectedApartment) return;

        try {
            await deleteApartment(selectedApartment.apartment_id).unwrap();
            toast.success('Apartment deleted successfully!');
            setShowDeleteModal(false);
            setSelectedApartment(null);
            refetch();
        } catch (error: any) {
            console.error('Failed to delete apartment:', error);
            if (error?.status === 400) {
                toast.error(error?.data?.message || 'Cannot delete apartment with active bookings');
            } else {
                toast.error('Failed to delete apartment');
            }
        }
    };

    // Handle availability management
    const handleManageAvailability = async () => {
        if (!selectedApartment || availabilityForm.dates.length === 0) return;

        try {
            await manageAvailability({
                apartment_id: selectedApartment.apartment_id,
                dates: availabilityForm.dates,
                is_available: availabilityForm.is_available,
                reason: availabilityForm.reason || null
            }).unwrap();
            toast.success('Apartment availability updated successfully!');
            setShowAvailabilityModal(false);
            setAvailabilityForm({ dates: [], is_available: false, reason: '' });
            refetch();
        } catch (error: any) {
            console.error('Failed to manage availability:', error);
            toast.error(error?.data?.message || 'Failed to update availability');
        }
    };

    // Get status color
    const getStatusColor = (isActive: boolean) => {
        return isActive 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800';
    };

    // Get availability color
    const getAvailabilityColor = (isAvailable: boolean) => {
        return isAvailable 
            ? 'bg-blue-100 text-blue-800' 
            : 'bg-red-100 text-red-800';
    };

    // Generate page numbers for pagination
    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            const startPage = Math.max(1, currentPage - 2);
            const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
        }

        return pages;
    };

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <div className="flex items-center">
                        <XCircle className="h-6 w-6 text-red-600 mr-3" />
                        <div>
                            <h3 className="text-lg font-semibold text-red-800">Error Loading Apartments</h3>
                            <p className="text-red-600 mt-1">There was an error loading the apartments. Please try again.</p>
                            <button
                                onClick={() => refetch()}
                                className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Apartment Management</h1>
                        <p className="text-gray-600">Manage apartments and availability</p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={handleCreate}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Create Apartment
                        </button>
                        <button
                            onClick={() => refetch()}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                        >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Apartments</p>
                            <p className="text-3xl font-bold text-gray-900">{totalApartments}</p>
                        </div>
                        <div className="p-3 bg-blue-100 rounded-full">
                            <Building2 className="h-6 w-6 text-blue-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Active Apartments</p>
                            <p className="text-3xl font-bold text-green-600">{activeApartments}</p>
                        </div>
                        <div className="p-3 bg-green-100 rounded-full">
                            <CheckCircle className="h-6 w-6 text-green-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Available</p>
                            <p className="text-3xl font-bold text-blue-600">{availableApartments}</p>
                        </div>
                        <div className="p-3 bg-purple-100 rounded-full">
                            <Users className="h-6 w-6 text-purple-600" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                                type="text"
                                placeholder="Search apartments..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <div className="relative">
                            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <select
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value as 'all' | 'true' | 'false');
                                    setCurrentPage(1);
                                }}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="all">All Status</option>
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                        </div>
                        <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                                type="text"
                                placeholder="Filter by location"
                                value={locationFilter}
                                onChange={(e) => {
                                    setLocationFilter(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Apartments Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-900">Apartments ({filteredApartments.length})</h2>
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600">Items per page:</label>
                            <select
                                value={itemsPerPage}
                                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                                className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                            </select>
                        </div>
                    </div>
                </div>

                {filteredApartments.length === 0 ? (
                    <div className="p-12 text-center">
                        <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No apartments found</h3>
                        <p className="text-gray-600 mb-4">No apartments match your current filters.</p>
                        <button
                            onClick={handleCreate}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                            Create Your First Apartment
                        </button>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Capacity</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Available</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {paginatedApartments.map((apartment: Apartment) => (
                                    <tr key={apartment.apartment_id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            {apartment.images && apartment.images.length > 0 ? (
                                                <img
                                                    src={apartment.images[0]}
                                                    alt={apartment.name}
                                                    className="w-16 h-16 object-cover rounded-lg"
                                                />
                                            ) : (
                                                <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                                                    <ImageIcon className="h-6 w-6 text-gray-400" />
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm font-medium text-gray-900">{apartment.name}</div>
                                            {apartment.description && (
                                                <div className="text-sm text-gray-500 mt-1 line-clamp-1">{apartment.description}</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-900">{apartment.location}</div>
                                            {apartment.address && (
                                                <div className="text-xs text-gray-500">{apartment.address}</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center text-sm text-gray-900">
                                                <Users className="h-4 w-4 mr-1 text-gray-400" />
                                                {apartment.capacity}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(apartment.is_active)}`}>
                                                {apartment.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getAvailabilityColor(apartment.is_available)}`}>
                                                {apartment.is_available ? 'Available' : 'Unavailable'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => handleView(apartment)}
                                                    className="text-green-600 hover:text-green-700 p-2 hover:bg-green-50 rounded-lg transition-all duration-200 border border-transparent hover:border-green-300 hover:shadow-sm"
                                                    title="View apartment details"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleEdit(apartment)}
                                                    className="text-green-600 hover:text-green-700 p-2 hover:bg-green-50 rounded-lg transition-all duration-200 border border-transparent hover:border-green-300 hover:shadow-sm"
                                                    title="Edit apartment"
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleAvailabilityModalOpen(apartment)}
                                                    className="text-purple-600 hover:text-purple-900 p-1 rounded hover:bg-purple-50 transition-colors"
                                                    title="Manage availability"
                                                >
                                                    <Calendar className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteModalOpen(apartment)}
                                                    className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                                                    title="Delete apartment"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="bg-white rounded-lg shadow border border-gray-100 p-4 mt-6">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-600">
                                Showing page {currentPage} of {totalPages} ({filteredApartments.length} total apartments)
                            </div>

                            <div className="flex items-center space-x-2">
                                {/* First Page */}
                                <button
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="First page"
                                >
                                    <ChevronsLeft className="w-4 h-4" />
                                </button>

                                {/* Previous Page */}
                                <button
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Previous page"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>

                                {/* Page Numbers */}
                                <div className="flex items-center space-x-1">
                                    {getPageNumbers().map((pageNum) => (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                                currentPage === pageNum
                                                    ? 'bg-blue-600 text-white'
                                                    : 'text-gray-700 hover:bg-gray-100 border border-gray-300'
                                            }`}
                                        >
                                            {pageNum}
                                        </button>
                                    ))}
                                </div>

                                {/* Next Page */}
                                <button
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Next page"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>

                                {/* Last Page */}
                                <button
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Last page"
                                >
                                    <ChevronsRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>



            {/* Availability Management Modal */}
            <Modal
                isOpen={showAvailabilityModal}
                onClose={() => {
                    setShowAvailabilityModal(false);
                    setAvailabilityForm({ dates: [], is_available: false, reason: '' });
                }}
                title="Manage Apartment Availability"
            >
                <div className="space-y-4">
                    {selectedApartment && (
                        <div className="mb-4">
                            <h4 className="font-medium text-gray-900">{selectedApartment.name}</h4>
                            <p className="text-sm text-gray-600">{selectedApartment.location}</p>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Dates *</label>
                        <input
                            type="date"
                            onChange={(e) => {
                                if (e.target.value) {
                                    setAvailabilityForm({
                                        ...availabilityForm,
                                        dates: [...availabilityForm.dates, e.target.value]
                                    });
                                    e.target.value = '';
                                }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {availabilityForm.dates.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                                {availabilityForm.dates.map((date, index) => (
                                    <span
                                        key={index}
                                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                                    >
                                        {formatDateUtil(date)}
                                        <button
                                            onClick={() => {
                                                setAvailabilityForm({
                                                    ...availabilityForm,
                                                    dates: availabilityForm.dates.filter((_, i) => i !== index)
                                                });
                                            }}
                                            className="ml-2 text-blue-600 hover:text-blue-800"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={availabilityForm.is_available}
                                onChange={(e) => setAvailabilityForm({ ...availabilityForm, is_available: e.target.checked })}
                                className="mr-2"
                            />
                            <span className="text-sm font-medium text-gray-700">Make available (uncheck to block)</span>
                        </label>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reason (Optional)</label>
                        <input
                            type="text"
                            value={availabilityForm.reason}
                            onChange={(e) => setAvailabilityForm({ ...availabilityForm, reason: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="e.g., Maintenance work"
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowAvailabilityModal(false);
                                setAvailabilityForm({ dates: [], is_available: false, reason: '' });
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleManageAvailability}
                            disabled={isManagingAvailability || availabilityForm.dates.length === 0}
                        >
                            {isManagingAvailability ? 'Updating...' : 'Update Availability'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                title="Delete Apartment"
            >
                <div className="space-y-4">
                    {selectedApartment && (
                        <>
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                                    <Trash2 className="h-6 w-6 text-red-600" />
                                </div>
                                <div>
                                    <h4 className="font-medium text-gray-900">{selectedApartment.name}</h4>
                                    <p className="text-sm text-gray-600">{selectedApartment.location}</p>
                                </div>
                            </div>

                            <p className="text-gray-600 mb-4">
                                Are you sure you want to delete this apartment? This action cannot be undone.
                                {selectedApartment.bookings_count && selectedApartment.bookings_count > 0 && (
                                    <span className="block mt-2 text-red-600 font-medium">
                                        Warning: This apartment has {selectedApartment.bookings_count} booking(s). 
                                        You cannot delete apartments with active bookings.
                                    </span>
                                )}
                            </p>
                        </>
                    )}

                    <div className="flex gap-3 pt-4">
                        <Button
                            variant="outline"
                            onClick={() => setShowDeleteModal(false)}
                            className="flex-1"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={isDeleting}
                            className="flex-1"
                        >
                            {isDeleting ? 'Deleting...' : 'Delete Apartment'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ApartmentManagement;

